import * as THREE from 'three';
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ColladaLoader } from 'three/examples/jsm/loaders/ColladaLoader.js';
import { GLTFExporter } from 'three/examples/jsm/exporters/GLTFExporter.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader.js';
import { Renderer } from './Renderer';
import { Scene } from './Scene';
import { Env } from './Env';
import { ID } from './ID';

export class Model3d {

    static unload(object = Scene.scene, callback = () => { }) {
        if (object !== undefined) {
            try {
                const currentObject = object.getObjectByName('shadowMesh') as THREE.Mesh;
                if (!currentObject) throw '';
                currentObject.geometry.dispose();
                if ('map' in currentObject.material)
                    (currentObject.material.map as unknown as THREE.Texture).dispose();
                const currentParent = currentObject.parent as THREE.Object3D;
                currentParent.remove(currentObject);
            } catch (e) {
                console.debug(e);
            }
            for (const id of ID.accessories) {
                try {
                    const currentObject = object.getObjectById(id) as THREE.Object3D;
                    const currentParent = currentObject?.parent as THREE.Object3D;
                    currentParent.remove(currentObject);
                    delete ID.accessories[ID.accessories.findIndex(element => element === id)];
                } catch (e) {
                    console.debug(e);
                }
            }
            for (const id of ID.meshs) {
                try {
                    const mesh = object.getObjectById(id) as THREE.Mesh;
                    if (mesh) {
                        if (mesh.geometry) {
                            const geometryId = mesh.geometry.id;
                            mesh.geometry.dispose();
                            delete ID.geometry[ID.geometry.findIndex(element => element === geometryId)];
                        }
                        if ((mesh.material as THREE.Material).isMaterial) {
                            const material = mesh.material as THREE.Material;
                            if ('map' in material && material.map) {
                                const meshMaterialMap = material.map as unknown as THREE.Texture;
                                const mapId = meshMaterialMap.id;
                                meshMaterialMap.dispose();
                                delete ID.textures[ID.textures.findIndex(element => element === mapId)];
                            }
                            const meshId = material.id;
                            material.dispose();
                            delete ID.materials[ID.materials.findIndex(element => element === meshId)];
                        }
                    }
                    if (mesh && mesh.parent) {
                        mesh.parent.remove(mesh);
                        delete ID.meshs[ID.meshs.findIndex(element => element === id)];
                    }
                } catch (e) {
                    console.debug(e);
                }
            }
            for (const id of ID.cabinets.items) {
                try {
                    const currentObject = object.getObjectById(id) as THREE.Object3D;
                    const currentParent = currentObject?.parent as THREE.Object3D;
                    currentParent.remove(currentObject);
                    delete ID.cabinets.items[ID.cabinets.items.findIndex(element => element === id)];
                } catch (e) {
                    console.debug(e);
                }
            }
            Renderer.renderer.renderLists.dispose();
        }
        if (callback) {
            callback();
        }
    }

    static raycaster = new THREE.Raycaster();
    static intersectRaycaster(objectsArray, object1, object2) {
        if (!object1.isVector3) {
            object1.updateMatrix();
            object1 = new THREE.Vector3(object1.matrixWorld.elements[12], object1.matrixWorld.elements[13], object1.matrixWorld.elements[14]);
        }
        if (!object2.isVector3) {
            object2.updateMatrix();
            object2 = new THREE.Vector3(object2.matrixWorld.elements[12], object2.matrixWorld.elements[13], object2.matrixWorld.elements[14]);
        }
        Model3d.raycaster.set(object1.clone(), object2.clone());
        return Model3d.raycaster.intersectObjects(objectsArray);
    }

    static async setModels() {
        const models_url = {};
        const promiseAll: any[] = [];
        if (Env.cabinets_cart_data) {
            for (const index in Env.cabinets_cart_data) {
                for (const i in Env.cabinets_cart_data[index]['3d_model']) {
                    const url = resolveURL(Env.cabinets_cart_data[index]['3d_model'][i].url);
                    if (!models_url[url]) {
                        models_url[url] = true;
                        promiseAll.push(loadModels(Env.cabinets_cart_data[index]['3d_model'][i], i));
                    }
                }
            }
        } else {
            for (const i in Env.panels_position['3d_model']) {
                const url = resolveURL(Env.panels_position['3d_model'][i].url);
                if (!models_url[url]) {
                    models_url[url] = true;
                    promiseAll.push(loadModels(Env.panels_position['3d_model'][i], i));
                }
            }
        }

        return Promise.all(promiseAll);

        function resolveURL(url: string): string {
            url = url.replace('../../../', '');
            return url;
        }

        async function loadModels(model_3d, i) {
            return new Promise(async function (resolve, reject) {
                switch (model_3d.type) {
                    case 'Collada': {
                        const collada = await new ColladaLoader().loadAsync(`${Env.base_url}${model_3d.url}`);
                        THREE.Cache.add(model_3d.url, transformLoadObject(collada.scene.children[0], i));
                        resolve(collada);
                    } break;
                    case 'OBJ': {
                        const obj = await new OBJLoader().loadAsync(`${Env.base_url}${model_3d.url}`);
                        THREE.Cache.add(model_3d.url, transformLoadObject(obj, i));
                        resolve(obj);
                    } break;
                    case 'GLTF': {
                        const gltf = await new GLTFLoader().loadAsync(`${Env.base_url}${model_3d.url}`);
                        THREE.Cache.add(model_3d.url, await transformLoadObject(gltf.scene.children[0], i));
                        resolve(gltf);
                    } break;
                    default: {
                        reject('Can not load this type model.');
                    }
                }
            })
        }

        function transformLoadObject(object, name) {
            const material = new THREE.MeshMatcapMaterial();
            const geometries: any[] = [];

            object.traverse((node) => {
                if (node.isMesh) {
                    node.castShadow = true;
                    node.receiveShadow = true;
                }
                if (node.type === 'LineSegments') {
                    node.visible = false;
                }
                if (node.geometry) {
                    let clonedGeometry = node.geometry.clone();
                    clonedGeometry.applyMatrix4(node.matrixWorld);
                    for (const key in clonedGeometry.attributes) {
                        if (key === 'position' || key === 'normal') continue;
                        clonedGeometry.deleteAttribute(key);
                    }
                    geometries.push(clonedGeometry);
                }
                if (node.material !== undefined) {
                    if (node.material.map) {
                        node.material.map.dispose();
                        delete node.material.map;
                    }
                    node.material.dispose();
                    delete node.material;
                    node.material = material;
                    node.material.needUpdate = true;
                    let url;
                    if (name === 'noga') {
                        url = `${Env.base_url}static/corps/matcaps_black_ball.jpg`;
                    } else {
                        url = `${Env.base_url}static/corps/matcaps_metal.jpg`;
                    }
                    if (Env.textures.find(texture => texture.userData.url === url)) {
                        node.material.matcap = Env.textures.find(texture => texture.userData.url === url);
                    } else {
                        const image = THREE.Cache.get(url);
                        const texture = new THREE.CanvasTexture(image);
                        texture.userData = {
                            url: url
                        };
                        Env.textures.push(texture);
                        node.material.matcap = texture;
                    }
                }
                if (node.name.includes('camera')) {
                    node.aspect = Env.canvas_size.width / Env.canvas_size.height;
                }
            });
            let url;
            if (name === 'noga') {
                url = `${Env.base_url}static/corps/matcaps_black_ball.jpg`;
            } else {
                url = `${Env.base_url}static/corps/matcaps_metal.jpg`;
            }
            if (Env.textures.find(texture => texture.userData.url === url)) {
                material.matcap = Env.textures.find(texture => texture.userData.url === url);
            } else {
                const image = THREE.Cache.get(url);
                const texture = new THREE.CanvasTexture(image);
                texture.userData = {
                    url: url
                };
                Env.textures.push(texture);
                material.matcap = texture;
            }
            const mergeGeometry = BufferGeometryUtils.mergeGeometries(geometries);
            return new THREE.Mesh(mergeGeometry, material);
        }
    }

    static async gltfExport(object = Scene.scene) {
        new GLTFExporter()
            .parse(
                object,
                async (gltf) => {
                    if (gltf instanceof ArrayBuffer) {
                        self.postMessage({ gltf: gltf, isString: false })
                    } else {
                        const string = JSON.stringify(gltf, null, 2);
                        self.postMessage({ gltf: string, isString: true })
                    }

                },
                (error) => {
                    console.debug(error)
                },
                {
                    trs: false,
                    onlyVisible: true,
                    binary: true,
                    maxTextureSize: 8192,
                    // embedImages: true
                }
            )
    }
}